import { makeAutoObservable } from 'mobx';
import { groupByFunction } from './utils/rxjs/groupByFunction';
import { map, mergeMap } from 'rxjs/operators';
import { isOrderOpen } from './utils/mungingUtils/orderStatusFilter';
import { tacticMap } from '../../classes/tactics/tacticKeys';
import { extractTradePlanMarketDataHealth } from '../../utils/marketDataHealth';

export class TradePlanStore {
  root;
  positionStream;

  // map {4234234: {}}

  currentSetupInfoByTradePlanId = {};

  // map {4234234: {...tradePlan}, 23423423: {...tradePlan}}
  tradePlans = {};

  //map {'AMD': { 12341: 'Live', 234453: 'Simulated', 4555: 'Live'}}
  tradePlansBySymbol = {};
  // map of arrays {4234234: [12312, 123123], 23423423: [534534, 345345]}
  softOrderIdsByTradePlan = {};
  pendingTradePlans = {};
  pendingTradePlanTimers = {};

  // map {12312: {...softOrder}, 123123: {...softOrder}, 534534: {...softOrder}}
  softOrdersById = {};

  // map {123213: {...position}, 45363: {...position}}
  // idea for optimization, instead of replacing object, just update necessary fields
  positions = {
    tradeUnafraid: {},
    tradier: {},
  };
  // map {123213: {...position}, 45363: {...position}}
  orders = {
    tradeUnafraid: {},
    tradier: {},
  };
  ordersByTradePlanId = {};
  // array [{...execution}, {...execution}, {...execution}], these messages come in one by one. All current days executions are sent on load, one by one.
  executions = [];
  executionKeys = {};

  constructor(root) {
    makeAutoObservable(this);
    this.root = root;
    this.initializeStreams();
  }

  initializeStreams = () => {
    //trade plan stream
    const tradePlanStream = groupByFunction(
      'messageType',
      'tradePlans',
      this.root.tradePlanWebsocketSubject
    );
    const removeTradePlanStream = groupByFunction(
      'messageType',
      'removeTradePlan',
      this.root.tradePlanWebsocketSubject
    );
    tradePlanStream.subscribe(this.handleTradePlanMessage);
    removeTradePlanStream.subscribe(this.handleRemoveTradePlanMsg);
    // remove alert trade plan, so we can remove the related soft orders in this store

    const removeAlertStream = groupByFunction(
      'messageType',
      'removeAlertTradePlan',
      this.root.tradePlanWebsocketSubject
    );
    removeAlertStream.subscribe(this.handleRemoveAlertTradePlanStream);

    // soft orders stream
    groupByFunction(
      'messageType',
      'softOrder',
      this.root.websocketSubject
    ).subscribe((msg) => {
      this.handleSoftOrderMessage(msg);
    });

    groupByFunction(
      'messageType',
      'removeSoftOrder',
      this.root.websocketSubject
    ).subscribe(({ tacticId }) => {
      this.handleRemoveSoftOrderMessage(tacticId);
    });

    //positions stream
    this.positionStream = groupByFunction(
      'messageType',
      'positions',
      this.root.websocketSubject
    ).pipe(
      map((positionMessage) => {
        const accountId = positionMessage?.positions?.accountId;
        return positionMessage.positions.position.map((position) => ({
          ...position,
          accountId: position.accountId ?? accountId,
        }));
      }),
      mergeMap((p) => p)
    );
    this.positionStream.subscribe((msg) => this.handlePositionMessage(msg));

    //orders stream
    this.orderStream = groupByFunction(
      'messageType',
      'orderStatus',
      this.root.websocketSubject
    ).pipe(
      map((orderMessage) => orderMessage.orders.order),
      mergeMap((p) => p)
    );
    this.orderStream.subscribe((msg) => this.handleOrderMessage(msg));

    //executions stream
    this.executionsStream = groupByFunction(
      'messageType',
      'execution',
      this.root.websocketSubject
    );
    this.executionsStream.subscribe((msg) => this.handleExecutionMessage(msg));

    this.closedPositionStream = groupByFunction(
      'messageType',
      'closedPosition',
      this.root.websocketSubject
    );
    this.closedPositionStream.subscribe((msg) =>
      this.handleClosedPositionMessage(msg)
    );
  };

  buildExecutionKey = ({
    tradePlanId,
    tradingSystemId,
    symbol,
    quantity,
    fillPrice,
    executionTime,
  }) =>
    [
      tradePlanId ?? '',
      tradingSystemId ?? '',
      symbol ?? '',
      quantity ?? '',
      fillPrice ?? '',
      executionTime ?? '',
    ].join('|');

  addExecutionIfMissing = (execution) => {
    const key = this.buildExecutionKey(execution);
    const existingIndex = this.executionKeys[key];
    if (existingIndex !== undefined) {
      const existing = this.executions[existingIndex];
      this.executions[existingIndex] = this.mergeExecutionDetails(
        existing,
        execution
      );
      return;
    }

    this.executionKeys[key] = this.executions.length;
    this.executions.push(execution);
  };

  mergeExecutionDetails = (existing, incoming) => {
    if (!existing) return incoming;
    if (!incoming) return existing;

    const merged = { ...existing };
    Object.entries(incoming).forEach(([key, value]) => {
      if (this.hasUsefulExecutionValue(value)) {
        merged[key] = value;
      }
    });
    return merged;
  };

  hasUsefulExecutionValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value !== 'string') return true;

    const normalized = value.trim().toLowerCase();
    return (
      normalized !== '' &&
      normalized !== 'none' &&
      normalized !== 'bid: unknown ask: unknown'
    );
  };

  buildExecutionFromOrder = (order) => {
    const quantity = Number(order.last_fill_quantity ?? order.exec_quantity ?? 0);
    const fillPrice = Number(order.last_fill_price ?? order.avg_fill_price ?? 0);
    const priceMultiplier = this.resolveOrderPriceMultiplier(order);

    if (!order.tradePlanId || !order.tradingSystemId || !quantity || !fillPrice) {
      return null;
    }

    return {
      symbol: order.symbol,
      action: order.side,
      quantity,
      tradingSystemId: order.tradingSystemId,
      tradePlanId: order.tradePlanId,
      fillPrice,
      accountId: order.accountId,
      totalCost: fillPrice * quantity * priceMultiplier,
      commission: 0,
      executionTime: order.transaction_date,
      quoteAtTimeOfOrder: 'bid: unknown ask: unknown',
      underlyingQuoteAtTimeOfFill: 'bid: unknown ask: unknown',
      reason: order.reason_description ?? '',
    };
  };

  resolveOrderPriceMultiplier = (order) => {
    const explicitMultiplier = [
      order.priceMultiplier,
      order.price_multiplier,
      order.contractMultiplier,
      order.contract_multiplier,
      order.multiplier,
    ].find((value) => this.isFiniteNumber(value) && Number(value) > 0);

    if (explicitMultiplier != null) {
      return Number(explicitMultiplier);
    }

    const classType = `${order.class ?? order.classType ?? ''}`.toLowerCase();
    if (!classType.includes('option')) {
      return 1;
    }

    return classType.includes('future') ? 1 : 100;
  };

  symbolsMatch = (left, right) => {
    if (!left || !right) return false;
    const lhs = `${left}`;
    const rhs = `${right}`;
    return lhs === rhs || lhs.startsWith(rhs) || rhs.startsWith(lhs);
  };

  hasDirectPositionAssociation = (tradePlan, position, tradePlanId) => {
    if (!tradePlan || !position) return false;

    const tradePlanTradingSystemId = `${tradePlan.tradingSystemId ?? ''}`;
    const positionTradingSystemId = `${position.tradingSystemId ?? ''}`;

    return (
      (tradePlanTradingSystemId &&
        positionTradingSystemId &&
        positionTradingSystemId === tradePlanTradingSystemId) ||
      `${position.tradePlanId ?? ''}` === `${tradePlanId}` ||
      `${position.id ?? ''}` === `${tradePlan.positionId ?? ''}`
    );
  };

  isTradePlanManagedPosition = (position) => {
    if (!position) return false;

    return Object.values(this.tradePlans).some((tradePlan) => {
      if (!tradePlan?.inPosition) return false;

      const tradePlanAccountId = `${tradePlan.accountNumber ?? ''}`;
      const positionAccountId = `${position.accountId ?? ''}`;
      const tradePlanBrokerage = `${tradePlan.brokerage ?? ''}`;
      const positionBrokerage = `${position.brokerage ?? ''}`;

      if (
        tradePlanAccountId &&
        positionAccountId &&
        tradePlanAccountId !== positionAccountId
      ) {
        return false;
      }

      if (
        tradePlanBrokerage &&
        positionBrokerage &&
        tradePlanBrokerage !== positionBrokerage
      ) {
        return false;
      }

      return (
        this.symbolsMatch(position.symbol, tradePlan.positionSymbol) ||
        this.symbolsMatch(position.symbol, tradePlan.symbol)
      );
    });
  };

  positionMatchesTradePlanAccountAndBrokerage = (tradePlan, position) => {
    if (!tradePlan || !position) return false;

    const tradePlanAccountId = `${tradePlan.accountNumber ?? ''}`;
    const positionAccountId = `${position.accountId ?? ''}`;
    const tradePlanBrokerage = `${tradePlan.brokerage ?? ''}`;
    const positionBrokerage = `${position.brokerage ?? ''}`;

    if (
      tradePlanAccountId &&
      positionAccountId &&
      tradePlanAccountId !== positionAccountId
    ) {
      return false;
    }

    if (
      tradePlanBrokerage &&
      positionBrokerage &&
      tradePlanBrokerage !== positionBrokerage
    ) {
      return false;
    }

    return true;
  };

  scoreTradePlanPosition = (tradePlan, position, tradePlanId) => {
    if (!tradePlan || !position) return Number.NEGATIVE_INFINITY;

    let score = 0;
    const positionSymbol = position.symbol ?? '';
    const tradePlanSymbol = tradePlan.symbol ?? '';
    const trackedPositionSymbol = tradePlan.positionSymbol ?? '';

    if (`${position.tradePlanId ?? ''}` === `${tradePlanId}`) score += 20;
    if (`${position.id ?? ''}` === `${tradePlan.positionId ?? ''}`) score += 40;

    if (positionSymbol === trackedPositionSymbol) score += 120;
    else if (this.symbolsMatch(positionSymbol, trackedPositionSymbol)) score += 80;

    if (positionSymbol === tradePlanSymbol) score += 30;
    else if (this.symbolsMatch(positionSymbol, tradePlanSymbol)) score += 15;

    if (
      trackedPositionSymbol &&
      positionSymbol.startsWith(tradePlanSymbol) &&
      positionSymbol.length > tradePlanSymbol.length
    ) {
      score += 25;
    }

    return score;
  };

  registerPendingTradePlan = (tradePlan) => {
    if (!tradePlan?.id) return;

    const id = `${tradePlan.id}`;
    this.clearPendingTradePlan(id);
    const pendingTradePlan = this.createPendingTradePlan(tradePlan);
    this.pendingTradePlans[id] = pendingTradePlan;
    this.tradePlans[id] = pendingTradePlan;
    const symbol = pendingTradePlan.symbol;
    if (symbol) {
      if (this.tradePlansBySymbol[symbol]) {
        this.tradePlansBySymbol[symbol][id] = pendingTradePlan.dataSource;
      } else {
        this.tradePlansBySymbol[symbol] = {
          [id]: pendingTradePlan.dataSource,
        };
      }
    }
    this.pendingTradePlanTimers[id] = window.setTimeout(() => {
      this.clearPendingTradePlan(id);
    }, 30000);
  };

  createPendingTradePlan = (tradePlan) => ({
    ...this.normalizeTradePlanMarketDataHealth({
      ...tradePlan,
      id: `${tradePlan.id}`,
      inPosition: false,
      paused: false,
      expanded: false,
      inPersistingState: false,
      createTimeStamp: tradePlan.createTimeStamp ?? tradePlan.id,
      restartSettings: {
        deactivated: false,
        ...(tradePlan.restartSettings ?? {}),
      },
      setupDetails: tradePlan.setupDetails ?? {},
      entryTactics: tradePlan.entryTactics ?? {},
      exitTactics: tradePlan.exitTactics ?? {},
      setupFilters: tradePlan.setupFilters ?? {},
      positionTactics: tradePlan.positionTactics ?? {},
      __pending: true,
    }),
  });

  normalizeTradePlanMarketDataHealth = (tradePlan) => {
    const health = extractTradePlanMarketDataHealth(tradePlan);

    return {
      ...tradePlan,
      marketDataHealthStatusNormalized: health.status,
      marketDataHealthStatusRaw: health.raw,
      marketDataHealthStatusSource: health.source,
    };
  };

  isSoftOrderDebugEnabled = () => {
    if (typeof window === 'undefined') return false;

    return (
      window.config?.REACT_APP_DEBUG_SOFT_ORDERS === 'true' ||
      window.localStorage?.getItem('tuui.debugSoftOrders') === 'true'
    );
  };

  getSoftOrderIdsSnapshot = (tradePlanId) => [
    ...(this.softOrderIdsByTradePlan[tradePlanId] ?? []),
  ];

  debugSoftOrderEvent = (event, details) => {
    if (!this.isSoftOrderDebugEnabled()) return;

    console.debug(`[tuui:softOrders] ${event}`, {
      at: new Date().toISOString(),
      ...details,
    });
  };

  summarizeSoftOrder = (softOrder) => ({
    tradePlanId: softOrder?.tradePlanId,
    tacticId: softOrder?.value?.tacticId,
    tactic: softOrder?.value?.tactic,
    symbol: softOrder?.value?.symbol,
  });

  clearPendingTradePlan = (id) => {
    if (id === null || id === undefined) return;

    const key = `${id}`;
    const timer = this.pendingTradePlanTimers[key];
    if (timer) {
      window.clearTimeout(timer);
      delete this.pendingTradePlanTimers[key];
    }

    if (this.pendingTradePlans[key]) {
      delete this.pendingTradePlans[key];
    }

    if (this.tradePlans[key]?.__pending) {
      this.removeTradePlanFromStoreInternal(key);
    }
  };

  //MESSAGE HANDLERS
  handleTradePlanMessage = (msg) => {
    const tradePlans = msg.tradePlans;
    if (msg.snapshot) {
      this.debugSoftOrderEvent('tradePlan snapshot received', {
        tradePlanIds: Object.keys(tradePlans ?? {}),
        softOrderIdsByTradePlan: Object.entries(this.softOrderIdsByTradePlan).reduce(
          (acc, [tradePlanId, ids]) => {
            acc[tradePlanId] = [...ids];
            return acc;
          },
          {}
        ),
      });
      const nextIds = new Set(Object.keys(tradePlans ?? {}));
      Object.keys(this.tradePlans).forEach((id) => {
        if (!nextIds.has(id)) {
          if (this.shouldRetainTradePlanDuringSnapshot(id)) {
            return;
          }
          this.removeTradePlanFromStore(id);
        }
      });
    }
    if (tradePlans) {
      for (const id in tradePlans) {
        this.clearPendingTradePlan(id);
        const expandedState = this.tradePlans[id]
          ? this.tradePlans[id].expanded
          : false;
        this.tradePlans[id] = this.normalizeTradePlanMarketDataHealth(tradePlans[id]);
        this.tradePlans[id].expanded = expandedState;
        if (tradePlans[id].inPosition && this.currentSetupInfoByTradePlanId[id])
          delete this.currentSetupInfoByTradePlanId[id];
        const symbol = tradePlans[id].symbol;
        if (this.tradePlansBySymbol[symbol]) {
          this.tradePlansBySymbol[symbol][id] = tradePlans[id].dataSource;
        } else {
          this.tradePlansBySymbol[symbol] = {
            [id]: tradePlans[id].dataSource,
          };
        }
      }
    }
  };

  handleRemoveTradePlanMsg = (msg) => {
    this.clearPendingTradePlan(msg.tradePlanId);
    this.removeTradePlanFromStore(msg.tradePlanId);
  };

  shouldRetainTradePlanDuringSnapshot = (tradePlanId) => {
    const tradePlan = this.tradePlans[tradePlanId];
    if (!tradePlan?.inPosition) return false;

    return Boolean(this.getTradePlanPosition(tradePlanId));
  };

  removeTradePlanFromStore = (id) => {
    this.clearPendingTradePlan(id);
    this.removeTradePlanFromStoreInternal(id);
  };

  removeTradePlanFromStoreInternal = (id) => {
    if (this.tradePlans[id]) {
      const tradePlan = this.tradePlans[id];
      Object.entries(this.positions.tradeUnafraid).forEach(([positionKey, position]) => {
        if (!this.hasDirectPositionAssociation(tradePlan, position, id)) {
          return;
        }

        const shouldRestoreToBrokerBucket =
          !position?.tradePlanId && !position?.tradingSystemId;
        if (shouldRestoreToBrokerBucket && position?.id) {
          this.positions.tradier[position.id] = {
            ...position,
          };
        }
        delete this.positions.tradeUnafraid[positionKey];
      });

      const symbol = this.tradePlans[id].symbol;
      delete this.tradePlans[id];
      if (this.softOrderIdsByTradePlan[id]) {
        this.debugSoftOrderEvent('removeTradePlan clearing softOrders', {
          tradePlanId: id,
          ids: this.getSoftOrderIdsSnapshot(id),
        });
        this.softOrderIdsByTradePlan[id].forEach(
          (softOrder) => delete this.softOrdersById[softOrder]
        );
        delete this.softOrderIdsByTradePlan[id];
      }
      if (this.currentSetupInfoByTradePlanId[id])
        delete this.currentSetupInfoByTradePlanId[id];
      if (this.tradePlansBySymbol[symbol]?.[id])
        delete this.tradePlansBySymbol[symbol][id];
      if (this.tradePlansBySymbol[symbol] && Object.keys(this.tradePlansBySymbol[symbol]).length === 0) {
        delete this.tradePlansBySymbol[symbol];
      }
    }
  };

  handleRemoveAlertTradePlanStream = (msg) => {
    const id = msg.tradePlanId;
    if (this.softOrderIdsByTradePlan[id]) {
      this.debugSoftOrderEvent('removeAlertTradePlan clearing softOrders', {
        tradePlanId: id,
        ids: this.getSoftOrderIdsSnapshot(id),
      });
      this.softOrderIdsByTradePlan[id].forEach(
        (softOrder) => delete this.softOrdersById[softOrder]
      );
      delete this.softOrderIdsByTradePlan[id];
    }
    if (this.currentSetupInfoByTradePlanId[id])
      delete this.currentSetupInfoByTradePlanId[id];
  };

  handleSoftOrderMessage = (msg) => {
    const tradePlanId = msg.tradePlanId;
    const softOrderId = msg.value.tacticId;
    const beforeIds = this.getSoftOrderIdsSnapshot(tradePlanId);
    if (this.softOrderIdsByTradePlan[tradePlanId]) {
      if (!this.softOrderIdsByTradePlan[tradePlanId].includes(softOrderId)) {
        this.softOrderIdsByTradePlan[tradePlanId] = [
          ...this.softOrderIdsByTradePlan[tradePlanId],
          softOrderId,
        ];
      }
    } else {
      this.softOrderIdsByTradePlan[tradePlanId] = [softOrderId];
    }
    if (this.softOrdersById[softOrderId]) {
      this.softOrdersById[softOrderId] = {
        ...this.softOrdersById[softOrderId],
        ...msg,
      };
    } else this.softOrdersById[softOrderId] = msg;
    this.debugSoftOrderEvent('softOrder add/update', {
      ...this.summarizeSoftOrder(msg),
      beforeIds,
      afterIds: this.getSoftOrderIdsSnapshot(tradePlanId),
      beforeSize: beforeIds.length,
      afterSize: this.getSoftOrderIdsSnapshot(tradePlanId).length,
    });
    // currentSoftOrder handler
    //still need to finish the rest of the setupDetailParsers
    // maybe make icons for each type of filter and a passing/not passing version of each

    const tactic = msg.value.tactic;
    const category = tacticMap[tactic].category;
    if (category === 'Setup') {
      const detailParser = tacticMap[tactic].setupDetailParser;
      const setupDetails = detailParser(this.root, msg);
      if (this.currentSetupInfoByTradePlanId[tradePlanId]) {
        this.currentSetupInfoByTradePlanId[tradePlanId].setupDetails =
          setupDetails.details;
        this.currentSetupInfoByTradePlanId[tradePlanId].triggerPrice =
          setupDetails.triggerPrice;
      } else {
        this.currentSetupInfoByTradePlanId[tradePlanId] = {
          triggerPrice: setupDetails.triggerPrice,
          setupDetails: setupDetails.details,
          filters: {},
        };
      }
    }
    if (category === 'Setup Filter') {
      const filterStatus = tacticMap[tactic].filterStatusParser(this.root, msg);
      const filter = msg.value.tactic;
      if (this.currentSetupInfoByTradePlanId[tradePlanId]) {
        this.currentSetupInfoByTradePlanId[tradePlanId].filters[
          msg.value.tactic
        ] = filterStatus;
      } else {
        this.currentSetupInfoByTradePlanId[tradePlanId] = {
          triggerPrice: false,
          setupDetails: null,
          filters: {
            [filter]: filterStatus,
          },
        };
      }
    }
  };

  removeSoftOrderIdFromAllTradePlans = (tacticId) => {
    const affectedTradePlanIds = [];

    Object.entries(this.softOrderIdsByTradePlan).forEach(([tradePlanId, ids]) => {
      if (!ids?.includes(tacticId)) return;

      affectedTradePlanIds.push(tradePlanId);
      this.softOrderIdsByTradePlan[tradePlanId] = ids.filter(
        (id) => id !== tacticId
      );
    });

    return affectedTradePlanIds;
  };

  handleRemoveSoftOrderMessage = (tacticId) => {
    const existingSoftOrder = this.softOrdersById[tacticId];
    const tradePlanId = existingSoftOrder?.tradePlanId;
    const beforeIdsByTradePlan = {};

    if (tradePlanId) {
      beforeIdsByTradePlan[tradePlanId] =
        this.getSoftOrderIdsSnapshot(tradePlanId);
    } else {
      Object.entries(this.softOrderIdsByTradePlan).forEach(([id, ids]) => {
        if (ids?.includes(tacticId)) beforeIdsByTradePlan[id] = [...ids];
      });
    }

    if (this.softOrdersById[tacticId]) {
      delete this.softOrdersById[tacticId];
    }

    const affectedTradePlanIds = this.removeSoftOrderIdFromAllTradePlans(tacticId);
    const afterIdsByTradePlan = affectedTradePlanIds.reduce((acc, id) => {
      acc[id] = this.getSoftOrderIdsSnapshot(id);
      return acc;
    }, {});

    this.debugSoftOrderEvent('removeSoftOrder', {
      tacticId,
      tradePlanId,
      tactic: existingSoftOrder?.value?.tactic,
      hadSoftOrder: Boolean(existingSoftOrder),
      affectedTradePlanIds,
      beforeIdsByTradePlan,
      afterIdsByTradePlan,
    });
  };
  handleRemoveTuPosition = (msgId) => {
    delete this.positions.tradeUnafraid[msgId];
  };
  handleRemoveTradierPosition = (msgId) => {
    delete this.positions.tradier[msgId];
  };

  handlePositionMessage = (msg) => {
    const shouldStoreAsTradePlanPosition =
      Boolean(msg.tradePlanId) ||
      Boolean(msg.tradingSystemId) ||
      this.isTradePlanManagedPosition(msg);

    if (!shouldStoreAsTradePlanPosition) {
      if (this.positions.tradier[msg.id]) {
        if (msg.quantity <= 0) {
          this.handleRemoveTradierPosition(msg.id);
        } else {
          this.positions.tradier[msg.id] = {
            ...this.positions.tradier[msg.id],
            ...msg,
          };
        }
      } else {
        if (msg.quantity > 0) this.positions.tradier[msg.id] = msg;
      }
    } else {
      const key = msg.tradingSystemId ?? msg.id;
      if (this.positions.tradeUnafraid[key]) {
        if (msg.quantity <= 0) {
          delete this.positions.tradeUnafraid[key];
        } else {
          this.positions.tradeUnafraid[key] = {
            ...this.positions.tradeUnafraid[key],
            ...msg,
          };
        }
      } else {
        if (msg.quantity > 0) this.positions.tradeUnafraid[key] = msg;
      }
    }
  };

  handleOrderMessage = (msg) => {
    msg.id = msg.id.toString();
    const id = msg.id;
    if (!msg.tradePlanId) {
      this.orders.tradier[id] = msg;
    } else {
      this.orders.tradeUnafraid[id] = msg;
      if (this.ordersByTradePlanId[msg.tradePlanId])
        this.ordersByTradePlanId[msg.tradePlanId][id] = msg;
      else
        this.ordersByTradePlanId[msg.tradePlanId] = {
          [id]: msg,
        };
    }

    const syntheticExecution = this.buildExecutionFromOrder(msg);
    if (
      syntheticExecution &&
      `${msg.status}`.toUpperCase() === 'FILLED' &&
      msg.transaction_date &&
      msg.transaction_date !== 'notFilledYet'
    ) {
      this.addExecutionIfMissing(syntheticExecution);
    }
  };
  handleExecutionMessage = (msg) => {
    this.addExecutionIfMissing(msg.execution);
  };

  handleClosedPositionMessage = (msg) => {
    const closedTradingSystemId = msg?.closedPosition?.tradingSystemId;
    if (!closedTradingSystemId) return;

    Object.entries(this.tradePlans).forEach(([tradePlanId, tradePlan]) => {
      if (`${tradePlan?.tradingSystemId ?? ''}` !== `${closedTradingSystemId}`) {
        return;
      }

      this.tradePlans[tradePlanId] = {
        ...tradePlan,
        inPosition: false,
      };

      const tsKey = tradePlan?.tradingSystemId;
      if (tsKey && this.positions.tradeUnafraid[tsKey]) {
        delete this.positions.tradeUnafraid[tsKey];
      }

      Object.entries(this.positions.tradeUnafraid).forEach(([positionKey, position]) => {
        if (
          `${position?.tradePlanId ?? ''}` === `${tradePlanId}` ||
          `${position?.tradingSystemId ?? ''}` === `${closedTradingSystemId}`
        ) {
          delete this.positions.tradeUnafraid[positionKey];
        }
      });
    });
  };
  // MAPPING FUNCTIONS
  tradePlanIdMappingFunction = (msg) => {
    return msg.tradePlans.tradePlan.map((plan) => plan.id);
  };

  tradePlanMappingFunction = (msg) => {
    return msg.tradePlans.tradePlan.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  };

  //GETTERS
  //trade plans

  get tradePlanIdsSortedByCreateTime() {
    const arr = [...Object.keys(this.tradePlans)];
    return arr.sort((a, b) => {
      return (
        this.tradePlans[a].createTimeStamp - this.tradePlans[b].createTimeStamp
      );
    });
  }
  get tradePlanIdsSortedBySymbol() {
    const arr = [...Object.keys(this.tradePlans)];
    return arr.sort((a, b) => {
      return this.tradePlans[a].symbol.localeCompare(this.tradePlans[b].symbol);
    });
  }
  get tradePlanIdsSortedByStatus() {
    const arr = [...Object.keys(this.tradePlans)];
    return arr.sort((a, b) => {
      if (this.tradePlans[a].inPosition === this.tradePlans[b].inPosition)
        if (
          this.tradePlans[a].restartSettings.deactivated ===
          this.tradePlans[b].restartSettings.deactivated
        ) {
          return this.tradePlans[a].symbol.localeCompare(
            this.tradePlans[b].symbol
          );
        } else if (this.tradePlans[a].restartSettings.deactivated) return -1;
        else return -1;
      if (this.tradePlans[a].inPosition === false) return 1;
      else return -1;
    });
  }
  get tradePlanIdsSortedByTemplate() {
    const arr = [...Object.keys(this.tradePlans)];
    return arr.sort((a, b) => {
      const result = this.tradePlans[a].savedConfigurationTitle.localeCompare(
        this.tradePlans[b].savedConfigurationTitle
      );
      if (result === 0)
        return this.tradePlans[a].symbol.localeCompare(
          this.tradePlans[b].symbol
        );
      else return result;
    });
  }

  //positions
  get pendingTradePlanIds() {
    return Object.keys(this.pendingTradePlans).sort((left, right) => {
      return (
        (this.pendingTradePlans[right]?.createTimeStamp ?? 0) -
        (this.pendingTradePlans[left]?.createTimeStamp ?? 0)
      );
    });
  }

  get tuPositionList() {
    const positions = this.positions.tradeUnafraid;
    const dedupedPositionIds = [];
    const seenTradePlans = new Set();

    Object.values(positions).forEach((position) => {
      const tradePlanId = `${position?.tradePlanId ?? ''}`;
      if (!tradePlanId || tradePlanId === '0' || !this.tradePlans[tradePlanId]) {
        const positionKey = position?.tradingSystemId ?? position?.id;
        if (positionKey) dedupedPositionIds.push(positionKey);
        return;
      }

      if (seenTradePlans.has(tradePlanId)) return;

      const preferredPosition = this.getTradePlanPosition(tradePlanId);
      const preferredPositionKey =
        preferredPosition?.tradingSystemId ?? preferredPosition?.id;
      if (preferredPositionKey) {
        dedupedPositionIds.push(preferredPositionKey);
        seenTradePlans.add(tradePlanId);
      }
    });

    return dedupedPositionIds;
  }

  get tradierPositionList() {
    return Object.keys(this.positions.tradier);
  }

  getTradePlanPosition = (tradePlanId) => {
    const tradePlan = this.tradePlans[tradePlanId];
    if (!tradePlan) return null;

    const positions = [
      ...Object.values(this.positions.tradeUnafraid),
      ...Object.values(this.positions.tradier),
    ].filter(Boolean);
    const candidates = positions.filter((position) => {
      if (this.hasDirectPositionAssociation(tradePlan, position, tradePlanId)) {
        return true;
      }

      if (!tradePlan.inPosition) {
        return false;
      }

      if (!this.positionMatchesTradePlanAccountAndBrokerage(tradePlan, position)) {
        return false;
      }

      return (
        this.symbolsMatch(position.symbol, tradePlan.positionSymbol) ||
        this.symbolsMatch(position.symbol, tradePlan.symbol)
      );
    });

    if (candidates.length === 0) return null;

    return candidates.sort((left, right) => {
      return (
        this.scoreTradePlanPosition(tradePlan, right, tradePlanId) -
        this.scoreTradePlanPosition(tradePlan, left, tradePlanId)
      );
    })[0];
  };

  getTradePlanPositionMetrics = (tradePlanId) => {
    const tradePlan = this.tradePlans[tradePlanId];
    const position = this.getTradePlanPosition(tradePlanId);
    if (!tradePlan || !position) return null;

    const openQuantity = this.resolvePositionOpenQuantity(position);
    const entryQuantity = this.resolvePositionEntryQuantity(position, openQuantity);
    const priceMultiplier = this.resolvePositionPriceMultiplier(position, tradePlan);
    const fillPrice = this.resolvePositionFillPrice(position, openQuantity, priceMultiplier);
    const unrealizedPnl = this.resolvePositionUnrealizedPnl(
      position,
      tradePlan,
      fillPrice,
      openQuantity,
      priceMultiplier
    );
    const realizedPnl = this.isFiniteNumber(position?.realizedPnl)
      ? Number(position.realizedPnl)
      : null;

    return {
      position,
      openQuantity,
      entryQuantity,
      priceMultiplier,
      fillPrice,
      unrealizedPnl,
      realizedPnl,
    };
  };

  isFiniteNumber = (value) => Number.isFinite(Number(value));

  resolvePositionOpenQuantity = (position) => {
    const rawQuantity = this.isFiniteNumber(position?.quantity)
      ? Number(position.quantity)
      : this.isFiniteNumber(position?.currentQuantity)
        ? Number(position.currentQuantity)
        : null;
    return rawQuantity == null ? null : Math.abs(rawQuantity);
  };

  resolvePositionEntryQuantity = (position, fallbackQuantity) => {
    if (this.isFiniteNumber(position?.entryQuantity) && Number(position.entryQuantity) > 0) {
      return Number(position.entryQuantity);
    }
    return fallbackQuantity;
  };

  resolvePositionPriceMultiplier = (position, tradePlan) => {
    if (this.isFiniteNumber(position?.priceMultiplier) && Number(position.priceMultiplier) > 0) {
      return Number(position.priceMultiplier);
    }

    const instrument = `${position?.type ?? tradePlan?.instrument ?? ''}`.toLowerCase();
    if (instrument.includes('option')) {
      return 100;
    }
    return 1;
  };

  resolvePositionFillPrice = (position, openQuantity, priceMultiplier) => {
    if (this.isFiniteNumber(position?.avg_fill_price) && Number(position.avg_fill_price) > 0) {
      return Number(position.avg_fill_price);
    }

    const costBasis = this.isFiniteNumber(position?.cost_basis)
      ? Math.abs(Number(position.cost_basis))
      : this.isFiniteNumber(position?.costBasis)
        ? Math.abs(Number(position.costBasis))
        : null;

    if (
      costBasis != null &&
      openQuantity != null &&
      openQuantity > 0 &&
      priceMultiplier > 0
    ) {
      return costBasis / openQuantity / priceMultiplier;
    }

    return null;
  };

  resolvePositionUnrealizedPnl = (
    position,
    tradePlan,
    fillPrice,
    openQuantity,
    priceMultiplier
  ) => {
    if (this.isFiniteNumber(position?.uPnlAtBidAsk)) {
      return Number(position.uPnlAtBidAsk);
    }
    // A thin option can have a last trade without a current NBBO. Use that value for
    // display-only PnL; execution/risk continues to require uPnlAtBidAsk on the backend.
    if (this.isFiniteNumber(position?.uPnlAtLast)) {
      return Number(position.uPnlAtLast);
    }
    if (fillPrice == null || openQuantity == null || openQuantity <= 0) {
      return null;
    }

    const bid = this.isFiniteNumber(position?.bid) ? Number(position.bid) : null;
    const ask = this.isFiniteNumber(position?.ask) ? Number(position.ask) : null;
    const price = this.isFiniteNumber(position?.price)
      ? Number(position.price)
      : this.isFiniteNumber(position?.currentPrice)
        ? Number(position.currentPrice)
        : null;
    const isLong = position?.sideLong ?? tradePlan?.goLong ?? true;
    const valuationPrice =
      price ??
      (isLong ? bid ?? ask : ask ?? bid);

    if (valuationPrice == null) {
      return null;
    }

    return (isLong ? valuationPrice - fillPrice : fillPrice - valuationPrice) *
      openQuantity *
      priceMultiplier;
  };

  //orders
  get openTuOrderList() {
    return Object.values(this.orders.tradeUnafraid).reduce((acc, item) => {
      if (isOrderOpen(item.status, 'TRADIER')) {
        return [...acc, item.id];
      } else return acc;
    }, []);
  }
  get openTradierOrderList() {
    return Object.values(this.orders.tradeUnafraid).reduce((acc, item) => {
      if (isOrderOpen(item.status, 'TRADIER')) return [...acc, item.id];
      else return acc;
    }, []);
  }

  get tradeUnafraidOrderList() {
    return Object.keys(this.orders.tradeUnafraid);
  }
  get tradierOrderList() {
    return Object.keys(this.orders.tradier);
  }

  //executions

  get executionsSortByTime() {
    const arr = [...this.executions];
    return arr.sort((a, b) => {
      const aMillis = new Date(a.executionTime).getTime();
      const bMillis = new Date(b.executionTime).getTime();
      return bMillis - aMillis;
    });
  }

  // mutations

  flipExpandedState = (tradePlanId) => {
    this.tradePlans[tradePlanId].expanded =
      !this.tradePlans[tradePlanId].expanded;
  };

  expandAll = () => {
    Object.keys(this.tradePlans).forEach((id) => {
      this.tradePlans[id].expanded = true;
    });
  };
  collapseAll = () => {
    Object.keys(this.tradePlans).forEach((id) => {
      this.tradePlans[id].expanded = false;
    });
  };
}
